'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Users, Phone, Mail, MessageSquare, MoreHorizontal, Plus, Filter, ArrowUpRight, ArrowDownRight, Clock, CheckCircle2, XCircle, AlertCircle, Calendar, ExternalLink, DollarSign, Zap, RefreshCw } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ───────────────────────────────────────────────────────────────────

interface Lead {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  stage: PipelineStage;
  score: number;
  value: number;
  source: string;
  assignee: string;
  lastContact: string;
  avatar: string;
  tags: string[];
}

interface OutreachEvent {
  id: string;
  leadName: string;
  type: 'call' | 'email' | 'meeting' | 'linkedin' | 'sms';
  summary: string;
  outcome: 'positive' | 'neutral' | 'negative' | 'pending';
  date: string;
  assignee: string;
}

type PipelineStage = 'inquiry' | 'qualified' | 'proposal' | 'negotiation' | 'closed-won' | 'closed-lost';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const pipelineStages: { key: PipelineStage; label: string; color: string; bg: string }[] = [
  { key: 'inquiry',     label: 'Inquiry',      color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
  { key: 'qualified',   label: 'Qualified',    color: '#818cf8', bg: 'rgba(99,102,241,0.1)' },
  { key: 'proposal',    label: 'Proposal',     color: '#fbbf24', bg: 'rgba(245,158,11,0.1)' },
  { key: 'negotiation', label: 'Negotiation',  color: '#22d3ee', bg: 'rgba(6,182,212,0.1)' },
  { key: 'closed-won',  label: 'Closed Won',   color: '#34d399', bg: 'rgba(16,185,129,0.1)' },
  { key: 'closed-lost', label: 'Closed Lost',  color: '#f87171', bg: 'rgba(239,68,68,0.1)' },
];

const leads: Lead[] = [
  { id: 'l1', name: 'Sarah Mitchell', company: 'NovaTech Solutions', email: 'sarah@novatech.io', phone: '+971 50 123 4567', stage: 'proposal', score: 87, value: 48000, source: 'LinkedIn', assignee: 'Ryan A.', lastContact: '2h ago', avatar: 'SM', tags: ['IT Services', 'High Value'] },
  { id: 'l2', name: 'James Okafor', company: 'Meridian BPO', email: 'james@meridianbpo.com', phone: '+971 55 987 6543', stage: 'negotiation', score: 92, value: 72000, source: 'Referral', assignee: 'Layla K.', lastContact: '1d ago', avatar: 'JO', tags: ['BPO', 'Enterprise'] },
  { id: 'l3', name: 'Priya Sharma', company: 'CloudBridge Inc.', email: 'priya@cloudbridge.co', phone: '+971 52 456 7890', stage: 'qualified', score: 64, value: 22000, source: 'Website', assignee: 'Ryan A.', lastContact: '3d ago', avatar: 'PS', tags: ['VOIP', 'SMB'] },
  { id: 'l4', name: 'Marcus Webb', company: 'Apex Digital', email: 'marcus@apexdigital.ae', phone: '+971 56 321 0987', stage: 'closed-won', score: 95, value: 115000, source: 'Cold Outreach', assignee: 'Layla K.', lastContact: '5d ago', avatar: 'MW', tags: ['Digital Marketing', 'Retainer'] },
  { id: 'l5', name: 'Fatima Al-Rashid', company: 'Gulf Retail Group', email: 'fatima@gulfretail.ae', phone: '+971 54 654 3210', stage: 'inquiry', score: 41, value: 15000, source: 'Event', assignee: 'Omar S.', lastContact: '1h ago', avatar: 'FA', tags: ['Web Dev', 'New'] },
  { id: 'l6', name: 'David Chen', company: 'Synapse Analytics', email: 'david@synapse.io', phone: '+971 50 789 0123', stage: 'closed-lost', score: 55, value: 38000, source: 'LinkedIn', assignee: 'Ryan A.', lastContact: '2w ago', avatar: 'DC', tags: ['IT Services'] },
  { id: 'l7', name: 'Aisha Noor', company: 'Pinnacle Logistics', email: 'aisha@pinnacle.ae', phone: '+971 55 234 5678', stage: 'proposal', score: 78, value: 31000, source: 'Referral', assignee: 'Omar S.', lastContact: '6h ago', avatar: 'AN', tags: ['BPO', 'Mid-Market'] },
  { id: 'l8', name: 'Tom Hargreaves', company: 'Vantage Media', email: 'tom@vantage.media', phone: '+971 52 876 5432', stage: 'qualified', score: 69, value: 27500, source: 'Website', assignee: 'Layla K.', lastContact: '2d ago', avatar: 'TH', tags: ['Digital Marketing'] },
];

const outreachHistory: OutreachEvent[] = [
  { id: 'o1', leadName: 'James Okafor', type: 'call', summary: 'Discussed contract terms and SLA requirements. Client requested revised pricing.', outcome: 'positive', date: 'Today, 10:30 AM', assignee: 'Layla K.' },
  { id: 'o2', leadName: 'Sarah Mitchell', type: 'email', summary: 'Sent proposal deck for IT infrastructure overhaul. Awaiting review.', outcome: 'pending', date: 'Today, 9:15 AM', assignee: 'Ryan A.' },
  { id: 'o3', leadName: 'Fatima Al-Rashid', type: 'linkedin', summary: 'Connected and shared company profile. Scheduled discovery call.', outcome: 'positive', date: 'Yesterday, 4:00 PM', assignee: 'Omar S.' },
  { id: 'o4', leadName: 'Aisha Noor', type: 'meeting', summary: 'Demo of BPO platform. Client impressed with agent monitoring features.', outcome: 'positive', date: 'Yesterday, 2:30 PM', assignee: 'Omar S.' },
  { id: 'o5', leadName: 'Tom Hargreaves', type: 'email', summary: 'Follow-up on digital marketing audit report. No response yet.', outcome: 'neutral', date: 'Mon, 11:00 AM', assignee: 'Layla K.' },
  { id: 'o6', leadName: 'David Chen', type: 'call', summary: 'Final call — client chose competitor due to pricing. Marked as lost.', outcome: 'negative', date: 'Fri, 3:45 PM', assignee: 'Ryan A.' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(score: number) {
  if (score >= 80) return 'text-success';
  if (score >= 60) return 'text-warning';
  return 'text-danger';
}

function scoreBg(score: number) {
  if (score >= 80) return 'bg-success/10';
  if (score >= 60) return 'bg-warning/10';
  return 'bg-danger/10';
}

function stageStyle(stage: PipelineStage) {
  const s = pipelineStages.find(p => p.key === stage);
  return s ? { color: s.color, backgroundColor: s.bg } : {};
}

function stageLabel(stage: PipelineStage) {
  return pipelineStages.find(p => p.key === stage)?.label ?? stage;
}

const outreachIcons: Record<OutreachEvent['type'], React.ElementType> = {
  call: Phone, email: Mail, meeting: Calendar, linkedin: ExternalLink, sms: MessageSquare,
};

const outcomeStyles: Record<OutreachEvent['outcome'], string> = {
  positive: 'text-success bg-success/10',
  neutral:  'text-muted-foreground bg-muted/30',
  negative: 'text-danger bg-danger/10',
  pending:  'text-warning bg-warning/10',
};

const outcomeIcons: Record<OutreachEvent['outcome'], React.ElementType> = {
  positive: CheckCircle2, neutral: AlertCircle, negative: XCircle, pending: Clock,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, delta, deltaUp, sub }: {
  icon: React.ElementType; label: string; value: string; delta?: string; deltaUp?: boolean; sub?: string;
}) {
  return (
    <div className="card-surface p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon size={15} className="text-primary" />
        </div>
      </div>
      <div>
        <p className="text-metric text-foreground">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {delta && (
        <div className={`flex items-center gap-1 text-xs font-medium ${deltaUp ? 'text-success' : 'text-danger'}`}>
          {deltaUp ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
          {delta} vs last month
        </div>
      )}
    </div>
  );
}

function PipelineFunnel() {
  const stageCounts = pipelineStages.map(s => ({
    ...s,
    count: leads.filter(l => l.stage === s.key).length,
    value: leads.filter(l => l.stage === s.key).reduce((a, b) => a + b.value, 0),
  }));
  const maxCount = Math.max(...stageCounts.map(s => s.count), 1);

  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-sm font-semibold text-foreground">Pipeline Stages</h3>
        <span className="text-xs text-muted-foreground">{leads.length} total leads</span>
      </div>
      <div className="space-y-2.5">
        {stageCounts.map((stage) => (
          <div key={stage.key} className="group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium" style={{ color: stage.color }}>{stage.label}</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">${(stage.value / 1000).toFixed(0)}k</span>
                <span className="text-xs font-semibold text-foreground w-4 text-right">{stage.count}</span>
              </div>
            </div>
            <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${(stage.count / maxCount) * 100}%`, backgroundColor: stage.color }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConversionChart() {
  const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
  const rates = [28, 34, 31, 42, 38, 45];
  const max = Math.max(...rates);

  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-sm font-semibold text-foreground">Conversion Rate</h3>
        <div className="flex items-center gap-1.5 text-success text-xs font-medium">
          <ArrowUpRight size={13} />
          +7% this month
        </div>
      </div>
      <div className="flex items-end gap-2 h-28">
        {months.map((m, i) => (
          <div key={m} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-2xs text-muted-foreground font-tabular">{rates[i]}%</span>
            <div className="w-full rounded-t-sm transition-all duration-500" style={{
              height: `${(rates[i] / max) * 80}px`,
              background: i === months.length - 1
                ? 'linear-gradient(180deg, #3b82f6 0%, #06b6d4 100%)'
                : 'rgba(59,130,246,0.25)',
            }} />
            <span className="text-2xs text-muted-foreground">{m}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-3">
        {[
          { label: 'Avg Deal Size', value: '$41.2k' },
          { label: 'Win Rate', value: '45%' },
          { label: 'Sales Cycle', value: '18d' },
        ].map(item => (
          <div key={item.label} className="text-center">
            <p className="text-sm font-bold text-foreground">{item.value}</p>
            <p className="text-2xs text-muted-foreground mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadScoreBar({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-muted/40 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${score}%`,
            background: score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444',
          }}
        />
      </div>
      <span className={`text-xs font-bold w-7 text-right ${scoreColor(score)}`}>{score}</span>
    </div>
  );
}

function ProspectTable({ leads, activeStage }: { leads: Lead[]; activeStage: PipelineStage | 'all' }) {
  const filtered = activeStage === 'all' ? leads : leads.filter(l => l.stage === activeStage);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {['Contact', 'Stage', 'Lead Score', 'Deal Value', 'Source', 'Assignee', 'Last Contact', ''].map(h => (
              <th key={h} className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground py-3 px-3 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((lead) => (
            <tr key={lead.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors group">
              <td className="py-3 px-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-primary">{lead.avatar}</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">{lead.name}</p>
                    <p className="text-xs text-muted-foreground">{lead.company}</p>
                  </div>
                </div>
              </td>
              <td className="py-3 px-3">
                <span className="text-xs font-semibold px-2 py-1 rounded-full" style={stageStyle(lead.stage)}>
                  {stageLabel(lead.stage)}
                </span>
              </td>
              <td className="py-3 px-3 min-w-[120px]">
                <LeadScoreBar score={lead.score} />
              </td>
              <td className="py-3 px-3">
                <span className="font-semibold text-foreground font-tabular">${lead.value.toLocaleString()}</span>
              </td>
              <td className="py-3 px-3">
                <span className="text-xs text-muted-foreground">{lead.source}</span>
              </td>
              <td className="py-3 px-3">
                <span className="text-xs font-medium text-foreground">{lead.assignee}</span>
              </td>
              <td className="py-3 px-3">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock size={11} />
                  {lead.lastContact}
                </div>
              </td>
              <td className="py-3 px-3">
                <button className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md hover:bg-muted transition-all text-muted-foreground hover:text-foreground">
                  <MoreHorizontal size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="py-12 text-center text-muted-foreground text-sm">No leads in this stage</div>
      )}
    </div>
  );
}

function OutreachHistoryFeed({ events }: { events: OutreachEvent[] }) {
  return (
    <div className="space-y-3">
      {events.map((event) => {
        const TypeIcon = outreachIcons[event.type];
        const OutcomeIcon = outcomeIcons[event.outcome];
        return (
          <div key={event.id} className="flex gap-3 p-3 rounded-lg hover:bg-muted/20 transition-colors">
            <div className="w-8 h-8 rounded-lg bg-muted/40 flex items-center justify-center flex-shrink-0 mt-0.5">
              <TypeIcon size={14} className="text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="text-sm font-semibold text-foreground">{event.leadName}</span>
                  <span className="text-xs text-muted-foreground ml-2 capitalize">{event.type}</span>
                </div>
                <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${outcomeStyles[event.outcome]}`}>
                  <OutcomeIcon size={10} />
                  {event.outcome}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-2">{event.summary}</p>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-2xs text-muted-foreground">{event.date}</span>
                <span className="text-2xs text-muted-foreground">· {event.assignee}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CRMPage() {
  const [activeStage, setActiveStage] = useState<PipelineStage | 'all'>('all');

  const totalPipelineValue = leads.reduce((a, b) => a + b.value, 0);
  const wonLeads = leads.filter(l => l.stage === 'closed-won');
  const wonValue = wonLeads.reduce((a, b) => a + b.value, 0);
  const hotLeads = leads.filter(l => l.score >= 80).length;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-2xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight">Leads & CRM</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Customer acquisition pipeline and relationship management</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
              <Filter size={14} />
              Filter
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
              <RefreshCw size={14} />
              Sync
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
              <Plus size={14} />
              Add Lead
            </button>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={Users}       label="Total Leads"      value={String(leads.length)}                     delta="+12%"  deltaUp sub="Active pipeline" />
          <KpiCard icon={DollarSign}  label="Pipeline Value"   value={`$${(totalPipelineValue/1000).toFixed(0)}k`} delta="+18%" deltaUp sub="Across all stages" />
          <KpiCard icon={CheckCircle2} label="Closed Won"      value={`$${(wonValue/1000).toFixed(0)}k`}        delta="+23%"  deltaUp sub={`${wonLeads.length} deals`} />
          <KpiCard icon={Zap}         label="Hot Leads"        value={String(hotLeads)}                         delta="+3"    deltaUp sub="Score ≥ 80" />
        </div>

        {/* Middle Row: Pipeline Funnel + Conversion Chart + Outreach */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <PipelineFunnel />
          <ConversionChart />

          {/* Outreach History */}
          <div className="card-surface p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Outreach History</h3>
              <button className="text-xs text-primary hover:underline">View all</button>
            </div>
            <div className="overflow-y-auto max-h-64 scrollbar-thin">
              <OutreachHistoryFeed events={outreachHistory} />
            </div>
          </div>
        </div>

        {/* Prospect Contacts Table */}
        <div className="card-surface">
          <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Prospect Contacts</h3>
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin pb-0.5">
              <button
                onClick={() => setActiveStage('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeStage === 'all' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`}
              >
                All ({leads.length})
              </button>
              {pipelineStages.map(s => {
                const count = leads.filter(l => l.stage === s.key).length;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveStage(s.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeStage === s.key ? 'text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`}
                    style={activeStage === s.key ? { backgroundColor: s.bg, color: s.color } : {}}
                  >
                    {s.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>
          <ProspectTable leads={leads} activeStage={activeStage} />
        </div>

      </div>
    </AppLayout>
  );
}
